import { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { products, productCategories, warehouseFilters } from "@/mocks/products";

const sortOptions = [
  { label: "Best Selling", value: "sold" },
  { label: "Price: Low to High", value: "price_asc" },
  { label: "Price: High to Low", value: "price_desc" },
  { label: "Top Rated", value: "rating" },
];

const ProductsPage = () => {
  useEffect(() => {
    document.title = "All Products – USA & Canada Factory Wholesale | Crown Prime Mall";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute("content", "Browse 500+ verified USA and Canada factory products. Bulk wholesale orders from $6,000 USD, fast 2–5 day delivery, secure USD payments. Garden, farm, home goods, electronics and more.");
    }

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-products";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "All Products – Crown Prime Mall",
      "description": "Browse 500+ verified USA and Canada factory products. Bulk wholesale orders, fast delivery, secure USD payments.",
      "url": (import.meta.env.VITE_SITE_URL || "https://example.com") + "/products",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL || "https://example.com" },
          { "@type": "ListItem", "position": 2, "name": "Products", "item": (import.meta.env.VITE_SITE_URL || "https://example.com") + "/products" }
        ]
      }
    });
    if (!document.getElementById("schema-products")) {
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("schema-products");
      if (el) el.remove();
    };
  }, []);

  const [category, setCategory] = useState("All");
  const [warehouse, setWarehouse] = useState("All");
  const [sort, setSort] = useState("sold");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== "All") list = list.filter((p) => p.category === category);
    if (warehouse !== "All") list = list.filter((p) => p.warehouse === warehouse || p.warehouse === "Both");
    if (search) list = list.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()) || p.supplier.toLowerCase().includes(search.toLowerCase()));
    if (sort === "sold") list.sort((a, b) => b.sold - a.sold);
    else if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [category, warehouse, sort, search]);

  return (
    <div className="min-h-screen bg-[#fdf8f8]">
      {/* Top Bar */}
      <div className="bg-[#1a0a0a] text-white text-xs text-center py-2 px-4">
        FREE SHIPPING on orders over $50 to USA &amp; Canada &nbsp;|&nbsp; Secure USD Payments &bull; Verified Suppliers
      </div>

      {/* Navbar */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-[1400px] mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 cursor-pointer whitespace-nowrap flex-shrink-0">
            <img
              src="https://static.readdy.ai/image/3fc89005e5f8f541d0781d07714068dd/b31ed3570265bb91107b593ba40da4ad.jpeg"
              alt="Crown Prime Mall"
              className="h-9 w-9 rounded-full object-cover"
            />
            <div>
              <div className="font-bold text-sm text-[#C41E3A] leading-tight">Crown Prime Mall</div>
              <div className="text-[10px] text-gray-400">USA &amp; Canada Marketplace</div>
            </div>
          </Link>
          <div className="flex-1 max-w-lg hidden md:flex">
            <div className="flex w-full rounded-full overflow-hidden border-2 border-[#D4AF37]">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search products..."
                className="flex-1 px-4 py-2 text-sm outline-none"
              />
              <button className="bg-[#C41E3A] px-4 text-white cursor-pointer">
                <i className="ri-search-line text-sm"></i>
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/supplier-register" className="hidden md:flex items-center gap-1 bg-[#D4AF37] text-white text-xs px-4 py-2 rounded-full font-semibold hover:bg-[#b8962e] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-building-2-line"></i> Become a Supplier
            </Link>
            <Link to="/" className="text-gray-500 text-xs hover:text-[#C41E3A] transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-home-line mr-1"></i>Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Page Header */}
      <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B0000] py-10">
        <div className="max-w-[1400px] mx-auto px-6">
          <h1 className="text-2xl md:text-3xl font-extrabold text-white mb-1">All Products</h1>
          <p className="text-white/70 text-sm">
            {filtered.length} products from verified factory suppliers — shipped to USA &amp; Canada
          </p>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        {/* Filters Row */}
        <div className="flex flex-col md:flex-row gap-4 mb-8 items-start md:items-center justify-between">
          {/* Category */}
          <div className="flex flex-wrap gap-2">
            {productCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  category === cat ? "bg-[#C41E3A] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-[#C41E3A]"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          {/* Right filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-1">
              {warehouseFilters.map((w) => (
                <button
                  key={w}
                  onClick={() => setWarehouse(w)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    warehouse === w ? "bg-[#1a0a0a] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {w === "All" ? "All Warehouses" : w === "USA" ? "🇺🇸 USA" : w === "Canada" ? "🇨🇦 Canada" : "Both"}
                </button>
              ))}
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-xs border border-gray-200 rounded-lg px-3 py-2 outline-none bg-white cursor-pointer"
            >
              {sortOptions.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Product Grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-5xl"></i>
            </div>
            <p className="text-lg font-semibold">No products found</p>
            <p className="text-sm mt-1">Try adjusting your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {filtered.map((product) => (
              <Link
                key={product.id}
                to={`/products/${product.id}`}
                className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-[#C41E3A] transition-all group cursor-pointer block"
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                  />
                  {product.badge && (
                    <div className="absolute top-2 left-2 bg-[#C41E3A] text-white text-[10px] font-bold px-2 py-0.5 rounded">
                      {product.badge}
                    </div>
                  )}
                  <div className="absolute top-2 right-2 text-sm">
                    {product.warehouse === "USA" ? "🇺🇸" : product.warehouse === "Canada" ? "🇨🇦" : "🇺🇸🇨🇦"}
                  </div>
                </div>
                {/* Info */}
                <div className="p-4">
                  <div className="text-[10px] text-gray-400 mb-1">{product.supplier}</div>
                  <h3 className="font-semibold text-gray-900 text-xs leading-tight mb-2 line-clamp-2">{product.name}</h3>
                  <div className="flex items-center gap-1 mb-2">
                    <i className="ri-star-fill text-[#D4AF37] text-xs"></i>
                    <span className="text-xs text-gray-600 font-medium">{product.rating}</span>
                    <span className="text-[10px] text-gray-400">({product.reviews.toLocaleString()})</span>
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-base font-extrabold text-[#C41E3A]">${product.price}</span>
                    <span className="text-xs text-gray-400 line-through">${product.originalPrice}</span>
                  </div>
                  <div className="text-[10px] text-gray-400">
                    Min. order: {product.minOrder} {product.unit}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
